import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { CategoryTreeNode } from '../types';
import { apiClient } from '../api/client';

interface CategorySelectProps {
  value?: number;
  onChange: (categoryId: number | null) => void;
  placeholder?: string;
  className?: string;
}

interface CategoryOption {
  category: CategoryTreeNode;
  level: number;
  path: string;
}

const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  onChange,
  placeholder = "Select a category...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<CategoryTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (value) {
      updateSelectedCategoryName(value);
    } else {
      setSelectedCategoryName('');
    }
  }, [value, categories]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const tree = await apiClient.getCategoriesTree();
      setCategories(tree);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSelectedCategoryName = (categoryId: number) => {
    const findCategoryName = (cats: CategoryTreeNode[]): string => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          return cat.name;
        }
        const childResult = findCategoryName(cat.children);
        if (childResult) {
          return childResult;
        }
      }
      return '';
    };

    const name = findCategoryName(categories);
    setSelectedCategoryName(name);
  };

  const flattenCategories = (cats: CategoryTreeNode[], level = 0): CategoryOption[] => {
    const result: CategoryOption[] = [];

    cats.forEach(cat => {
      const path = level === 0 ? cat.name : `${'  '.repeat(level)}└─ ${cat.name}`;
      result.push({ category: cat, level, path });

      if (cat.children && cat.children.length > 0) {
        result.push(...flattenCategories(cat.children, level + 1));
      }
    });

    return result;
  };

  const options = flattenCategories(categories);

  const handleSelect = (categoryId: number) => {
    onChange(categoryId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left bg-white/5 border border-white/10 rounded-md text-white hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <span className={selectedCategoryName ? 'text-white' : 'text-slate-400'}>
          {selectedCategoryName || placeholder}
        </span>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-white/10 rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-slate-400">Loading categories...</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-slate-400">No categories available</div>
          ) : (
            options.map(({ category, level, path }) => (
              <button
                key={category.id}
                type="button"
                onClick={() => handleSelect(category.id)}
                className={`w-full px-3 py-2 text-left hover:bg-white/10 transition-colors ${
                  value === category.id ? 'bg-[var(--accent-gold)]/20 text-[var(--accent-gold)]' : 'text-white'
                }`}
              >
                <span style={{ paddingLeft: `${level * 12}px` }}>
                  {path}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CategorySelect;